import { Utils } from 'core/utils';
import { ControllerOptions, normalizedOptions, TextApplianceResult,
         TextController, TextControllerBase, TextEditingResult
       } from './text.controller';
import { TextControllerMethods, TextEditingRefusalReason
       } from './text.controller.methods';
import { FontDescription, Text, TextChunk } from './text.model';

export const ZoomModeFit: TextController = {
  setColor: TextControllerBase.setColor,
  setHorizontalAlignment: TextControllerBase.setHorizontalAlignment,
  setVerticalAlignment: TextControllerBase.setVerticalAlignment,
  setAvailableArea: TextControllerBase.setAvailableArea,

  setText: (
    model: Text,
    newText: Array<TextChunk>,
    rawOptions?: ControllerOptions
  ): TextEditingResult =>
  {
    const options = normalizedOptions(rawOptions);
    let workingModel = Utils.deepCopy(model);
    workingModel.text = TextControllerMethods.removeVariationSelectors(newText);

    const unicodeCheckResult =
        TextControllerMethods.checkForInvalidSymbols(workingModel);
    if (unicodeCheckResult !== TextEditingRefusalReason.None) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        reason: TextEditingRefusalReason.CharacterNotSupported,
        model: model,
        position: unicodeCheckResult
      };
    }

    const fitting = TextControllerMethods
      .reduceFontSizeChecked(workingModel, options.minFontSize);

    if (options.useAvailableArea) {
      TextControllerMethods.increaseFontSize(workingModel);
    }

    if (fitting.finalResult !== TextEditingRefusalReason.None) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        model: model,
        reason: fitting.finalResult,
        position: TextControllerMethods.locateProblemPosition(
            workingModel, fitting.finalResult),
      };
    }
    else {
      return <TextEditingResult>  {
        type: TextApplianceResult.Success,
        model: workingModel,
        reason: fitting.fixedResult,
      };
    }
  },

  setFont: (
    model: Text,
    fontDescription: FontDescription,
    rawOptions?: ControllerOptions
  ): TextEditingResult =>
  {
    const options = normalizedOptions(rawOptions);
    let workingModel = Utils.deepCopy(model);
    workingModel.fontName = fontDescription.name;
    workingModel.fontDescription = fontDescription;
    workingModel.leading = fontDescription.charDimensions.minLeading;

    const unicodeCheckResult =
        TextControllerMethods.checkForInvalidSymbols(workingModel);
    if (unicodeCheckResult !== TextEditingRefusalReason.None) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        reason: TextEditingRefusalReason.CharacterNotSupported,
        model: model,
        position: unicodeCheckResult
      };
    }

    const fitting = TextControllerMethods
      .reduceFontSizeChecked(workingModel, options.minFontSize);

    if (options.useAvailableArea) {
      TextControllerMethods.increaseFontSize(workingModel);
    }

    if (fitting.finalResult !== TextEditingRefusalReason.None) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        model: model,
        reason: fitting.finalResult,
        position: TextControllerMethods.locateProblemPosition(
          model, fitting.finalResult),
      };
    }
    else {
      return <TextEditingResult> {
        type: TextApplianceResult.Success,
        model: workingModel,
        reason: fitting.fixedResult,
      };
    }
  },

  setFontSize: (
    model: Text,
    fontSize: number,
    rawOptions?: ControllerOptions
  ): TextEditingResult =>
  {
    const options = normalizedOptions(rawOptions);
    const cappedFontSize = Math.min(fontSize, options.maxFontSize);

    let workingModel = Utils.deepCopy(model);
    workingModel.fontSize = cappedFontSize;

    if (workingModel.fontSize < options.minFontSize) {
      workingModel.fontSize = options.minFontSize;
      return <TextEditingResult> {
        type: TextApplianceResult.Success,
        model: workingModel,
        reason: TextEditingRefusalReason.MinFontSizeUndershot,
      };
    }

    const fitting = TextControllerMethods
      .reduceFontSizeChecked(workingModel, options.minFontSize);

    if (options.useAvailableArea) {
      TextControllerMethods.increaseFontSize(workingModel);
    }

    return <TextEditingResult> {
      type: TextApplianceResult.Success,
      model: workingModel,
      reason: fitting.fixedResult,
    };
  }
};

export const ZoomModeFitExpand: TextController = {
  setColor: TextControllerBase.setColor,
  setHorizontalAlignment: TextControllerBase.setHorizontalAlignment,
  setVerticalAlignment: TextControllerBase.setVerticalAlignment,
  setAvailableArea: TextControllerBase.setAvailableArea,

  setText: (
    model: Text,
    newText: Array<TextChunk>,
    rawOptions?: ControllerOptions
  ): TextEditingResult => ZoomModeFit.setText(
    model, newText, {...rawOptions, useAvailableArea: true }),

  setFont: (
    model: Text,
    fontDescription: FontDescription,
    rawOptions?: ControllerOptions
  ): TextEditingResult => ZoomModeFit.setFont(
    model, fontDescription, {...rawOptions, useAvailableArea: true }),

  setFontSize: (
    model: Text,
    fontSize: number,
    rawOptions?: ControllerOptions
  ): TextEditingResult => ZoomModeFit.setFontSize(
    model, fontSize, {...rawOptions, useAvailableArea: true }),
};



// WEBPACK FOOTER //
// ./src/app/text/text.controller.zoom-mode-fit.ts